// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { Font } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { MathUtils } from '@shared/utils/MathUtils';
import { TerceraChartBaseScaleRenderer, TerceraChartBaseScaleRendererSettings } from './TerceraChartBaseScaleRenderer';
export class TerceraChartNumberScaleRenderer extends TerceraChartBaseScaleRenderer {
    constructor(settings, terceraChart) {
        super(settings, terceraChart);
        this.Step = 0.000000001;
        this.Precision = 9;
        this.Legend = new Map();
        this.Format = '';
        this.window = null;
        this.AutoScaleRect = Rectangle.Empty();
        this.ScaleButtonRect = new Rectangle();
        this.ScaleButtonOffset = 32;
        this.isHoverScaleButton = false;
        this.SetClassName('TerceraChartNumberScaleRenderer');
    }
    calcStep(maxV, minV, maxC, minC, itemheight) {
        if (itemheight === undefined) {
            itemheight = 38;
        }
        let step = 10;
        const inter = TerceraChartNumberScaleRenderer.hpiTrue;
        const nstep = (itemheight * (maxV - minV)) / (maxC - minC);
        if (nstep > inter[0]) {
            step = inter[0];
        }
        else {
            for (let i = 1; i < inter.length; i++) {
                if (nstep >= inter[i] && nstep < inter[i - 1]) {
                    if (nstep - inter[i - 1] > inter[i - 1] - nstep) {
                        step = inter[i - 1];
                    }
                    else {
                        step = inter[i];
                    }
                }
            }
        }
        return MathUtils.RoundTo(step, this.Step);
    }
    calcStart(step, minV) {
        const astep = 1 / step;
        const st = Math.round(minV * astep);
        return st / astep;
    }
    /// <summary>
    /// Calculate preferred scale width
    /// Should know it before drawing to layout renderers
    /// </summary>
    GetPreferredWidth(gr) {
        let maxW = 0;
        const values = [this.window.FminFloatY, this.window.FmaxFloatY];
        for (let i = 0; i < values.length; i++) {
            const formattedValue = this.FormatValue(values[i]);
            gr.font = this.GetScaleFont().toString();
            const curW = gr.measureText(formattedValue).width;
            if (maxW < curW) {
                maxW = curW;
            }
        }
        if (this.settings.AutoScaleSwitcherVisible) {
            const manualW = gr.measureText('Manual').width;
            if (maxW < manualW) {
                maxW = manualW;
            }
        }
        return maxW + 5;
    }
    GetScaleFontHeight() {
        return this.settings.ScaleFont.Height;
    }
    GetScaleFont() {
        return this.settings.ScaleFont;
    }
    FormatValue(value) {
        const roundedValue = MathUtils.RoundTo(value, this.Step);
        const formatterValue = this.Legend.has(roundedValue)
            ? this.Legend.get(roundedValue)
            : PriceFormatter.formatPrice(roundedValue, this.Precision);
        return `${formatterValue}${this.Format}`;
    }
}
TerceraChartNumberScaleRenderer.hpiTrue = [
    10000000000, 5000000000, 1000000000, 500000000, 100000000, 50000000, 10000000, 5000000, 1000000, 500000, 100000,
    50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 7.5, 5, 2.5, 1, 0.5, 0.2, 0.1, 0.05, 0.02,
    0.01, 0.005, 0.002, 0.001, 0.0005, 0.0002, 0.0001, 0.00005, 0.00002, 0.00001, 0.000005, 0.000002, 0.000001,
    0.0000005, 0.0000002, 0.0000001, 0.00000005, 0.00000002, 0.00000001, 0.000000005, 0.000000002, 0.000000001
];
export class TerceraChartNumberScaleRendererSettings extends TerceraChartBaseScaleRendererSettings {
    constructor() {
        super();
        this.priceScaleFont = new Font(10, ThemeManager.customFontFamily);
    }
    themeChanged() {
        super.ThemeChanged();
        this.scaleAxisColor = ThemeManager.colors.Chart_PriceScaleAxisColor;
        this.scaleBackColor = ThemeManager.colors.Chart_ScalesBackColor;
        this.scaleTextColor = ThemeManager.colors.TableForeColor;
        this.scaleGridColor = ThemeManager.colors.chartGridColor;
        this.priceScaleFont = ThemeManager.Fonts.Font_10F_regular.copy();
    }
}
